import { Base64 } from "js-base64"
import { useEffect } from "react"
import { getMetaVar } from "~/common/getMetaVar"
import { trackEvent } from "~/util/tracking"

// Events that Rails hands to the next page load through the analytics-event
// meta var. Sign-in and sign-out are full page requests, so the SPA that made
// them is gone before it can report anything.
const SERVER_EVENTS = ["sign_in", "sign_in_failed", "sign_out"] as const

type ServerEvent = (typeof SERVER_EVENTS)[number]

type ServerAnalyticsEvent = {
  name: ServerEvent
  properties: Record<string, unknown>
}

const parseEvent = (): ServerAnalyticsEvent | null => {
  const encoded = getMetaVar("analytics-event")
  if (!encoded) return null

  const decoded = Base64.decode(encoded)
  if (!decoded) return null

  try {
    const event = JSON.parse(decoded)
    if (!SERVER_EVENTS.includes(event?.name)) return null
    return { name: event.name, properties: event.properties ?? {} }
  } catch (error) {
    console.error("failed to parse analytics-event meta var", error)
    return null
  }
}

// The meta var lives for the whole document, so React StrictMode's double
// mount would otherwise report the event twice.
let ranOnce = false

export const useServerAnalyticsEvent = () => {
  useEffect(() => {
    if (ranOnce) return
    ranOnce = true

    const event = parseEvent()
    if (!event) return

    // The property shape is fixed by SessionsController, which the
    // AnalyticsEvents catalogue mirrors.
    trackEvent(event.name, event.properties as never)
  }, [])
}
